import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/common/components/ui/tabs";
import { ArrowLeft, Save, User, Shield, Loader2 } from "lucide-react";
import { notify } from "@/common/components/common/Notification";
import PersonalInfoTab from "./components/PersonalInfoTab";
import { Loader } from "@/common/components/ui";
import BankDetailsTab from "./components/BankDetails";
import { useQuery } from "@tanstack/react-query";
import UserManagementService from "@/services/FinEaseHomeAndAdmin/APIs/UserManagementService";
import { UserDetailsByIdResponse } from "@/utils/types/UserManagement/UserManagement";

const AdminUserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isView = location.pathname.includes("/view/");
  const isEdit = location.pathname.includes("/edit/");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  interface Address {
    address: string;
    addressLine2?: string;
    landmark?: string;
    city: string;
    state: string;
    pincode?: string;
    country?: string;
    isDefaultAddress?: boolean;
  }

  interface BankAccount {
    bankName: string;
    branchName: string;
    accountNumber: string;
  }

  interface UpiId {
    id: string;
    isVerified: boolean;
  }

  interface UserData {
    name: string;
    email: string;
    mobile: string;
    status: string;
    kycStatus: string;
    addresses: Address[];
    bankAccounts: BankAccount[];
    upiIds: UpiId[];
    pan: string;
    aadhar: string;
    dob: string;
    gender: "Male" | "Female" | "Other";
    occupation: string;
    incomeRange: string;
    [key: string]: any;
  }

  const [userData, setUserData] = useState<UserData>({
    name: "",
    email: "",
    mobile: "",
    status: "Active",
    kycStatus: "Pending",
    addresses: [
      {
        address: "",
        addressLine2: "",
        landmark: "",
        city: "",
        state: "",
        pincode: "",
        country: "",
        isDefaultAddress: false,
      },
    ],
    bankAccounts: [
      {
        bankName: "",
        branchName: "",
        accountNumber: "",
      },
    ],
    upiIds: [
      {
        id: "",
        isVerified: false,
      },
    ],
    pan: "",
    aadhar: "",
    dob: "",
    gender: "Male",
    occupation: "",
    incomeRange: "",
  });

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const {
    data: userDetailsResponse,
    isLoading: isLoadingUserDetails,
    isFetching: isFetchingUserDetails,
  } = useQuery<UserDetailsByIdResponse>({
    queryKey: ["user-details", id],
    queryFn: () => UserManagementService.getUserDetailsById(id ?? ""),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!userDetailsResponse?.success) {
      return;
    }

    const apiUser = (userDetailsResponse.data ?? {}) as Record<string, any>;
    const nameFromApi =
      apiUser.full_name ??
      [apiUser.first_name, apiUser.last_name].filter(Boolean).join(" ").trim();

    setUserData(prev => ({
      ...prev,
      name: nameFromApi ?? prev.name,
      email: apiUser.email ?? prev.email,
      mobile: apiUser.phone_number ?? prev.mobile,
      status: apiUser.status ?? prev.status,
      kycStatus: apiUser.kycStatus ?? apiUser.kyc_status ?? prev.kycStatus,
      pan: apiUser.pan ?? prev.pan,
      aadhar: apiUser.aadhar ?? prev.aadhar,
      addresses: apiUser.addresses ?? prev.addresses,
      bankAccounts: apiUser.bank_accounts ?? prev.bankAccounts,
      upiIds: apiUser.upi_ids ?? prev.upiIds,
    }));
  }, [userDetailsResponse]);

  const isLoadingExistingUser =
    Boolean(id) && (isLoadingUserDetails || isFetchingUserDetails);
  const shouldShowLoader = loading || isLoadingExistingUser;
  const loaderMessage = isLoadingExistingUser
    ? "Loading user details..."
    : isEdit
      ? "Updating IPO..."
      : "Creating IPO...";

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!userData.name) {
      errors.name = "Full Name is required";
    }
    if (!userData.email) {
      errors.email = "Email is required";
    }
    if (!userData.mobile) {
      errors.mobile = "Mobile Number is required";
    }
    return errors;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setValidationErrors({});

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      notify.error("Validation Failed", "Please check the form for errors");
      return;
    }

    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      notify.success(
        "User Updated",
        "User details have been saved successfully."
      );
      navigate("/admin/users");
    }, 1000);
  };

  const getListingStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "border-emerald-100 bg-emerald-50 text-emerald-600";
      case "listed":
        return "border-blue-100 bg-blue-50 text-blue-600";
      default:
        return "border-amber-100 bg-amber-50 text-amber-600";
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50">
      {shouldShowLoader && <Loader message={loaderMessage} />}
      <form
        id="ipo-form"
        onSubmit={handleSave}
        className="flex h-full flex-col"
      >
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-7xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() => navigate("/admin/user-management")}
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                      {isEdit
                        ? `Edit : ${userData?.name ?? "User"}`
                        : isView
                          ? `View : ${userData?.name ?? "User"}`
                          : "Create User"}
                    </h1>
                    {userData?.status && (
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[12px] font-bold uppercase tracking-wider ${getListingStatusColor(
                          userData.status
                        )}`}
                      >
                        {userData.status.charAt(0).toUpperCase() +
                          userData.status.slice(1)}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm font-medium text-slate-500">
                    Configure offering, financials, and timeline details for
                    users
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isView ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => navigate("/admin/user-management")}
                    className="h-10 bg-primary px-5 text-sm font-semibold text-white"
                  >
                    Back to List
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={loading}
                    className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/95"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isEdit ? "Save" : "Create User"}
                  </Button>
                )}
              </div>
            </div>
            {/* Tab navigation moved into IPODetailsTab */}
          </div>
        </div>

        <div className="mx-auto w-full max-w-7xl flex-1 p-6">
          <div className="w-full space-y-6">
            <div className="m-0 space-y-6 duration-300 animate-in fade-in slide-in-from-bottom-2">
              <Tabs
                defaultValue="profile"
                className="w-full space-y-6"
                value={activeTab}
                onValueChange={setActiveTab}
              >
                <div className="scrollbar-none overflow-x-auto border-b border-slate-200">
                  <div className="flex w-full items-center justify-between">
                    <TabsList className="h-12 w-fit justify-start gap-2 bg-transparent p-0">
                      <TabsTrigger
                        value="profile"
                        className="group flex h-12 items-center gap-2 rounded-none border-b-2 border-transparent px-6 text-sm font-medium text-slate-500 data-[state=active]:border-primary data-[state=active]:text-primary"
                      >
                        <User className="h-4 w-4" /> Personal Info
                      </TabsTrigger>
                      <TabsTrigger
                        value="kyc"
                        className="group flex h-12 items-center gap-2 rounded-none border-b-2 border-transparent px-6 text-sm font-medium text-slate-500 data-[state=active]:border-primary data-[state=active]:text-primary"
                      >
                        <Shield className="h-4 w-4" /> Bank Details
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </div>

                <TabsContent value="profile" className="mt-0">
                  <PersonalInfoTab
                    userData={userData}
                    setUserData={setUserData}
                    readOnly={isView}
                    validationErrors={validationErrors}
                  />
                </TabsContent>
                <TabsContent value="kyc" className="mt-0">
                  <BankDetailsTab
                    userData={userData}
                    setUserData={setUserData}
                    readOnly={isView}
                    validationErrors={validationErrors}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminUserForm;
